// Client for the ims-service annotation-import pipeline API (spec 2026-06-18).
// Reaches ims-service via the existing `/ims-service` gateway/router route, reusing the
// same bearer token + X-TENANT the core API uses.
import axios from 'axios';

import {getActiveTenant} from '@/keycloak';
import {updateToken} from '@/utils/token-utils';

const client = axios.create({
  baseURL: `${window.location.origin}/ims-service`,
  withCredentials: true,
});

client.interceptors.request.use(async config => {
  const token = await updateToken();
  config.headers = config.headers || {};
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const tenant = (getActiveTenant() || '').toString().trim().toLowerCase();
  if (tenant) {
    config.headers['X-TENANT'] = tenant;
  }
  return config;
});

export default {
  // payload: {pipeline, project, images?: [id], allImages?: bool}
  runPipeline(payload) {
    return client.post('v1/ims/pipelines/run', payload);
  },
  getRun(runUuid) {
    return client.get(`v1/ims/pipelines/runs/${runUuid}`);
  },
  // Ask a queued or in-progress run to stop. Cooperative: the worker notices between tile
  // batches, so a stop leaves no half-written results and no partially attributed
  // annotations. A run still in the queue stops immediately.
  stopRun(runUuid) {
    return client.post(`v1/ims/pipelines/runs/${runUuid}/stop`);
  },
  // Redraw a finished run's annotations at a different confidence cutoff. This is a
  // re-parse of the retained results file (seconds), not a re-run of the model (hours) —
  // the detector writes everything above its own 0.30 threshold into that file.
  reapplyThreshold(runUuid, insertionThreshold) {
    return client.post(`v1/ims/pipelines/runs/${runUuid}/reapply`, {insertionThreshold});
  },
  // The tissue mask a run produced, as a PNG blob (white = tissue). Fetched rather than
  // linked because the endpoint needs the bearer token, which a plain <a href> would not
  // send. Available from the moment tissue detection finishes — i.e. during a run.
  downloadTissueMask(runUuid) {
    return client.get(`v1/ims/pipelines/runs/${runUuid}/tissue-mask`, {responseType: 'blob'});
  },
  // The pipelines this deployment offers. One registry, served by ims-service, rendered by
  // every surface that lists pipelines — so adding one is a backend change, not an edit to
  // three Vue components that can then disagree with each other.
  getCatalog() {
    return client.get('v1/ims/pipelines/catalog');
  },
  // Whether a results file already exists for this image, so the model need not be re-run.
  // Resolved by slide name off the filesystem, so it still finds results after the run
  // record has been deleted. Returns {available, path?, producedAt?}.
  availableResults(pipeline, image) {
    return client.get('v1/ims/pipelines/available-results', {params: {pipeline, image}});
  },
  // Per-pipeline settings for this tenant. Returns {pipeline, insertionThreshold, isDefault}.
  getPipelineSettings(pipeline) {
    return client.get(`v1/ims/pipelines/settings/${pipeline}`);
  },
  savePipelineSettings(pipeline, insertionThreshold) {
    return client.put(`v1/ims/pipelines/settings/${pipeline}`, {insertionThreshold});
  },
  listRuns(params) {
    return client.get('v1/ims/pipelines/runs', {params});
  },
  // Delete a pipeline run and the annotations it inserted (the App Engine task-run is removed
  // separately by the caller). Returns {run_uuid, annotations_deleted}.
  deleteRun(runUuid) {
    return client.delete(`v1/ims/pipelines/runs/${runUuid}`);
  },
  // Poll an async deletion job (deleteRun now returns 202 {jobId} and the run + its annotations are
  // removed by the ims-service worker). Returns {status:'QUEUED'|'RUNNING'|'DONE'|'FAILED', error}.
  getDeletion(jobId) {
    return client.get(`v1/ims/deletions/${jobId}`);
  },
  // Delete a core image instance + everything referencing it. Deliberately NOT core's
  // `ImageInstance.delete()` (DELETE /api/imageinstance/{id}.json): core deletes the slide's
  // annotations one at a time in a single transaction and holds a write lock on the project row for
  // the whole cascade — hours on a dense slide, during which WSI uploads 502 and pipeline-run
  // deletes stall (incident 2026-08-27). ims-service does it as one batched, trigger-free
  // transaction. Returns 202 {jobId}; poll with pollDeletion.
  deleteImage(imageId) {
    return client.delete(`v1/ims/images/${imageId}`);
  },
  // Poll an async deletion job until it leaves QUEUED/RUNNING. DONE -> resolve; FAILED/timeout ->
  // throw so the caller can surface the error. 150 x 2s = 5 min ceiling.
  async pollDeletion(jobId, tries = 150, intervalMs = 2000) {
    for (let i = 0; i < tries; i++) {
      let {data} = await this.getDeletion(jobId);
      if (data.status === 'DONE') {
        return;
      }
      if (data.status === 'FAILED') {
        throw new Error(data.error || 'deletion failed');
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error('deletion timed out');
  },
  // Segmentation raster overlay (spec 2026-06-19): the UC pipeline stores a slide-aligned label
  // tile pyramid instead of vector annotations. Returns {exists, width, height, tile_size, levels,
  // resolutions, classes:[{name,index,term_id,color}], ...} for an image (exists:false if none).
  getSegmentation(imageId) {
    return client.get(`v1/ims/segmentation/${imageId}`);
  },
  // Request a PIMS upload ticket. ims-service resolves storage/project from the tenant binding
  // and signs the PIMS request, returning {uploadUrl, queryParams, headers, lisUploadTicket, ...}.
  // payload: {slideId, slideMetadata?}
  createUploadTicket(payload) {
    return client.post('v1/ims/upload-tickets', payload);
  },
  // Materialize the caller in Ims Core (identity key `iss|sub`) and, when the caller is a
  // tenant admin, bootstrap the tenant's project + storage + binding. Core resolves users by
  // reference and NEVER creates them, so without this the first Core call of a new user's
  // session returns 500 "Cannot find current user". In LIS deployments zaya-lis-fe makes this
  // call; standalone Ims calls it itself from main.js at login.
  // Idempotent — safe on every login. Returns {status, tenant, role, projectId, storageId?}.
  // Spec: specs/2026-07-31-standalone-identity-bootstrap/.
  syncSelf() {
    return client.post('v1/ims/sync/self');
  },
};
